import { DateTime, IANAZone } from 'luxon';
import { DatedProductRule } from 'types/policy';

export const MAX_DATED_PRODUCT_RULES = 50;
export const DATED_PRODUCT_FIELD_LABELS: Record<
  DatedProductRule['field'],
  string
> = {
  PRODUCT_TAG: 'Product tag',
  SKU: 'SKU',
  PRODUCT_NAME: 'Product name',
};

const trim = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

export const normalizeDatedProductRules = (
  value: unknown,
): DatedProductRule[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((rule) => ({
      id: trim(rule?.id),
      name: trim(rule?.name),
      enabled: rule?.enabled,
      field: rule?.field,
      operator: rule?.operator,
      value: trim(rule?.value).toLocaleLowerCase('en-US'),
      ...(rule && Object.prototype.hasOwnProperty.call(rule, 'purchaseScope')
        ? {purchaseScope: rule.purchaseScope} : {}),
      startDate: trim(rule?.startDate),
      endDate: trim(rule?.endDate),
      timeZone: trim(rule?.timeZone),
    }))
    .sort((left, right) => left.id.localeCompare(right.id));
};

export const getDatedProductRulesError = (value: unknown): string | null => {
  if (value === undefined) return null;
  if (!Array.isArray(value))
    return 'Saved product rules could not be read. Reset them before saving the policy.';
  if (value.length > MAX_DATED_PRODUCT_RULES)
    return `You can add up to ${MAX_DATED_PRODUCT_RULES} product rules.`;
  const ids = new Set<string>();
  for (const rule of value) {
    if (
      !rule ||
      typeof rule.id !== 'string' ||
      !/^[A-Za-z0-9_-]{1,80}$/.test(rule.id || '') ||
      ids.has(rule.id)
    )
      return 'Each product rule must have a unique valid ID. Remove and recreate the invalid rule.';
    ids.add(rule.id);
    if (typeof rule.name !== 'string' || !rule.name.trim())
      return 'Enter a rule name.';
    if (rule.name.trim().length > 100)
      return 'Use 100 characters or fewer for the rule name.';
    if (typeof rule.enabled !== 'boolean')
      return 'Choose whether each product rule is enabled.';
    if (!['PRODUCT_TAG', 'SKU', 'PRODUCT_NAME'].includes(rule.field))
      return 'Choose a product tag, SKU, or product name.';
    if (
      !['EQUALS', 'CONTAINS'].includes(rule.operator) ||
      (rule.field !== 'PRODUCT_NAME' && rule.operator !== 'EQUALS')
    )
      return 'Tags and SKUs require an exact match.';
    if (typeof rule.value !== 'string' || !rule.value.trim())
      return 'Enter a product value to match.';
    if (rule.value.trim().length > 255)
      return 'Use 255 characters or fewer for the product value.';
    if (Object.prototype.hasOwnProperty.call(rule, 'purchaseScope') &&
      !['ALL_PURCHASES', 'DATE_RANGE'].includes(rule.purchaseScope))
      return 'Choose all purchases or a purchase period.';
    if (rule.purchaseScope === 'ALL_PURCHASES') {
      if (rule.startDate !== '' || rule.endDate !== '' || rule.timeZone !== 'UTC')
        return 'All-purchase rules cannot include purchase dates or a local timezone.';
      continue;
    }
    if (
      ![rule.startDate, rule.endDate].every(
        (date) =>
          typeof date === 'string' &&
          /^\d{4}-\d{2}-\d{2}$/.test(date) &&
          DateTime.fromISO(date, { zone: 'UTC' }).isValid,
      )
    )
      return 'Enter valid purchase dates in YYYY-MM-DD format.';
    if (rule.startDate > rule.endDate)
      return 'The last purchase date must be on or after the first purchase date.';
    if (
      typeof rule.timeZone !== 'string' ||
      !IANAZone.isValidZone(rule.timeZone)
    )
      return 'Enter a valid timezone, such as America/Los_Angeles.';
  }
  return null;
};

export const describeDatedProductRule = (rule: DatedProductRule): string =>
  `${DATED_PRODUCT_FIELD_LABELS[rule.field] || 'Product'} ${
    rule.operator === 'CONTAINS' ? 'contains' : 'equals'
  } “${rule.value}”; ${rule.purchaseScope === 'ALL_PURCHASES'
    ? 'all purchases.'
    : `purchased ${rule.startDate} through ${rule.endDate} (inclusive, ${rule.timeZone}).`}`;
